// This code has been partially generated by an AI.
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  DestroyRef,
  inject,
  signal,
  Type,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroXMark } from '@ng-icons/heroicons/outline';
import { Message, ToastService } from '@app/core/services/toast.service';
import { SnackService } from '@app/core/services/snack.service';

export type Toast = Message & { id: number; progress: number };

export const MAX_VISIBLE_TOASTS = 2;
const TICK_MS = 50;
const CLOSE_TICKS = 5;

@Component({
  selector: 'if-notification',
  templateUrl: './notification.component.html',
  imports: [NgClass, NgIcon],
  changeDetection: ChangeDetectionStrategy.Eager,
  viewProviders: [provideIcons({ heroXMark })],
})
export class NotificationComponent implements AfterViewInit {
  readonly viewSnackContainer = viewChild.required('viewSnackContainer', { read: ViewContainerRef });
  readonly toasts = signal<Toast[]>([]);
  readonly activeToasts = computed(() => this.toasts().filter(toast => toast.progress > 0));
  readonly queuedCount = computed(() => Math.max(0, this.activeToasts().length - MAX_VISIBLE_TOASTS));
  readonly renderedToasts = computed(() => {
    let activeRank = 0;
    return this.toasts()
      .map(toast => ({ toast, rank: toast.progress <= 0 ? -1 : activeRank++ }))
      .filter(entry => entry.rank <= MAX_VISIBLE_TOASTS);
  });
  readonly paused = signal(false);
  private readonly toastService = inject(ToastService);
  private readonly snackService = inject(SnackService);
  private readonly destroyRef = inject(DestroyRef);
  private snackRef?: ComponentRef<unknown>;
  private ticker?: Subscription;
  private nextId = 0;

  constructor() {
    this.destroyRef.onDestroy(() => this.ticker?.unsubscribe());
  }

  ngAfterViewInit() {
    this.toastService.messages.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(message => this.addToast(message));

    this.snackService.currentSnack.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(componentType => {
      this.snackRef?.destroy();
      if (!componentType) return;
      this.snackRef = this.createSnackBar(componentType);
    });
  }

  close(id: number) {
    this.toasts.update(toasts => toasts.map(toast => (toast.id === id ? { ...toast, progress: 0 } : toast)));
  }

  private createSnackBar<T>(componentType: Type<T>): ComponentRef<T> {
    return this.viewSnackContainer().createComponent(componentType);
  }

  private addToast(message: Message) {
    this.toasts.update(toasts => [...toasts, { ...message, id: this.nextId++, progress: 100 }]);
    this.ticker ??= interval(TICK_MS).subscribe(() => this.tick());
  }

  // Only visible toasts count down, so queued ones get their full display time once they move up.
  private tick() {
    let activeRank = 0;
    this.toasts.update(toasts =>
      toasts
        .map(toast => {
          const isClosing = toast.progress <= 0;
          const isCounting = !isClosing && activeRank++ < MAX_VISIBLE_TOASTS && !this.paused();
          return isClosing || isCounting ? { ...toast, progress: toast.progress - 1 } : toast;
        })
        .filter(toast => toast.progress > -CLOSE_TICKS),
    );
    this.stopTickerIfEmpty();
  }

  private stopTickerIfEmpty() {
    if (this.toasts().length) return;
    this.ticker?.unsubscribe();
    this.ticker = undefined;
    // mouseleave is not guaranteed when the hovered element disappears
    this.paused.set(false);
  }
}
